import { useState } from "react";
import { useEmailSettings, useUpdateEmailSettings, useEmailStats } from "@platform/hooks";
import { PageHeader } from "../../components/shared/PageHeader";
import { Button, Input, Label, useToast } from "@nthamizh/ui";

type EmailSettings = NonNullable<ReturnType<typeof useEmailSettings>["data"]>;
type EmailStats = ReturnType<typeof useEmailStats>["data"];

export function PlatformEmailSettingsPage() {
  const { data: settings, isLoading } = useEmailSettings();
  const { data: stats } = useEmailStats();

  if (isLoading || !settings) {
    return (
      <div className="space-y-6">
        <PageHeader title="Email settings" subtitle="Password reset, welcome, and verification emails." />
        <p className="text-sm text-ds-text-muted">Loading…</p>
      </div>
    );
  }

  return <EmailSettingsForm settings={settings} stats={stats} />;
}

// Split out so its local form state can be initialized directly from
// settings via a plain useState initializer, not a useEffect syncing
// props into state — this component only ever mounts once settings has
// already loaded (the parent's loading gate above guarantees that), so
// there's no "re-sync when the query refetches" case to handle and no
// effect is needed at all. apiKey deliberately never gets a value from
// settings (it's write-only: the backend never returns the real key,
// only a masked last-4).
function EmailSettingsForm({ settings, stats }: { settings: EmailSettings; stats: EmailStats }) {
  const { toast } = useToast();
  const updateSettings = useUpdateEmailSettings();

  const [apiKey, setApiKey] = useState("");
  const [fromEmail, setFromEmail] = useState(settings.fromEmail ?? "");
  const [fromName, setFromName] = useState(settings.fromName ?? "");
  const [enabled, setEnabled] = useState(settings.enabled);
  const [monthlyLimit, setMonthlyLimit] = useState(settings.monthlyLimit != null ? String(settings.monthlyLimit) : "");

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        provider: "resend",
        apiKey: apiKey.trim() || undefined, // blank = leave the existing key alone
        fromEmail: fromEmail.trim(),
        fromName: fromName.trim(),
        enabled,
        monthlyLimit: monthlyLimit.trim() ? Number(monthlyLimit) : null,
      });
      setApiKey("");
      toast({ title: "Email settings saved", variant: "success" });
    } catch {
      toast({ title: "Failed to save email settings", variant: "error" });
    }
  };

  const usagePercent = stats?.monthlyLimit ? Math.min(100, ((stats?.sentThisMonth ?? 0) / stats.monthlyLimit) * 100) : null;

  return (
    <div className="space-y-6">
      <PageHeader title="Email settings" subtitle="Password reset, welcome, and verification emails." />

      <div className="bg-white rounded-[12px] border border-ds-border p-5 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-ds-text">Sending is {enabled ? "on" : "off"}</p>
          <p className="text-xs text-ds-text-muted mt-0.5">
            {enabled
              ? "The platform will send password reset, welcome, and verification emails."
              : "No emails will be sent until this is turned on — including password reset, which will show an error to the user instead."}
          </p>
        </div>
        <button
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled((e) => !e)}
          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-gray-200"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : "translate-x-0"}`} />
        </button>
      </div>

      {stats?.monthlyLimit != null && (
        <div className="bg-white rounded-[12px] border border-ds-border p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-ds-text">This month&apos;s usage</p>
            <p className="text-sm text-ds-text-secondary">{stats.sentThisMonth} / {stats.monthlyLimit}</p>
          </div>
          <div className="h-2 bg-ds-bg rounded-full overflow-hidden">
            <div
              className={`h-full ${usagePercent && usagePercent >= 90 ? "bg-red-500" : "bg-indigo-500"}`}
              style={{ width: `${usagePercent ?? 0}%` }}
            />
          </div>
          <p className="text-xs text-ds-text-muted mt-2">Resets automatically on the 1st of each month.</p>
        </div>
      )}

      <div className="bg-white rounded-[12px] border border-ds-border p-5 space-y-4">
        <div>
          <p className="text-sm font-semibold text-ds-text">Provider</p>
          <p className="text-xs text-ds-text-muted mt-0.5">Resend — more providers can be added later without changing anything else.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="apiKey">API key</Label>
            <Input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={settings.hasApiKey ? `Leave blank to keep •••• ${settings.apiKeyLast4}` : "re_..."}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="monthlyLimit">Monthly send limit</Label>
            <Input
              id="monthlyLimit"
              type="number"
              min={1}
              value={monthlyLimit}
              onChange={(e) => setMonthlyLimit(e.target.value)}
              placeholder="No limit"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fromEmail">From address</Label>
            <Input
              id="fromEmail"
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="noreply@tailseed.com"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fromName">From name</Label>
            <Input
              id="fromName"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Tailseed"
            />
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <Button onClick={handleSave} loading={updateSettings.isPending}>Save changes</Button>
        </div>
      </div>
    </div>
  );
}
